import clsx from 'clsx';
import { useFirmClient } from '../../../FirmClient';
import { AssessmentEditableField } from '../AssessmentEditableField';
import { EditAssessmentPageContext } from '../../context/EditAssessmentPage.context';
import { useContext } from 'react';
import { useAssessmentEditableField } from '../../hooks/useAssessmentEditableField';

interface HouseholdHealthTileProps {
  firmID: string;
  householdID: string;
  firmClientID: string;
  className?: string;
  reactSummary: React.ReactNode;
  reactSummaryString: string;
}

/**
 * Renders a summary tile displaying a household's financial health, including score range, overall category, and an optional summary section.
 *
 * @param firmID - Unique identifier for the firm.
 * @param householdID - Unique identifier for the household.
 * @param firmClientID - Unique identifier for the firm client.
 * @param className - Additional CSS classes for the container.
 * @param reactSummary - Optional React node to display as a summary.
 *
 * @returns The household financial health summary component, or `null` if client data is unavailable.
 */
export function HouseholdHealthTile({
  firmID,
  className,
  reactSummary,
  firmClientID,
  reactSummaryString,
}: HouseholdHealthTileProps) {
  const { firmClient } = useFirmClient({ firmClientID, firmID });
  const editAssessment = useContext(EditAssessmentPageContext);

  const assessmentEditableField = useAssessmentEditableField({
    controlledContent: reactSummaryString,
    onExitEditMode: (content) => {
      if (editAssessment?.defaultContent.householdSummaryMarkdown) {
        editAssessment?.updateHouseholdSummaryMarkdown(
          content || editAssessment?.defaultContent.householdSummaryMarkdown
        );
      }
    },
    placeholder: editAssessment?.defaultContent.householdSummaryMarkdown,
  });

  return (
    firmClient && (
      <div
        className={clsx(
          'bg-darkMode-neutral/40 shadow-classic text-white p-6 md:p-8 mb-8 mt-[27px] rounded-3xl',
          className
        )}
      >
        <AssessmentEditableField
          readOnlyContent={reactSummary}
          textAreaClassName="!text-base"
          useAssessmentEditableFieldReturn={assessmentEditableField}
        />
      </div>
    )
  );
}
